import React from "react";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { Alert, ConfigProvider, Modal, Steps, message } from "antd";
import TitleBar from "../../common/title-bar/title-bar.js";
import CommonButton from "../../common/button/button.js";
import Text from "../../common/typography/text.js";
import RightSmallArrowIcon from "../../icons/right-small-arrow-icon.js";
import LeftSmallArrowIcon from "../../icons/left-small-arrow-icon.js";
import { colors } from "../../theme/colors.js";
import { useAuth } from "../AuthProvider.js";
import AccountDetailsStep from "./steps/AccountDetailsStep.js";
import ProductGroupsStep from "./steps/ProductGroupsStep.js";
import PricingStep from "./steps/PricingStep.js";
import {
  ACCOUNT_CONTACTS_KEY,
  BILLING_SAME_AS_SHIPPING_KEY,
  accountEnrollmentValidationSchema,
  applyBillingSameAsShipping,
  billingMatchesShipping,
  buildAccountContactsPayload,
  buildAccountDetailsPayload,
  buildAccountEnrollmentInitialValues,
  isAccountDetailsStepComplete,
} from "./accountEnrollmentFields.js";
import {
  buildPricingPayload,
  clearPricingForGroup,
  createEmptyPricingState,
  isPricingComplete,
} from "./accountEnrollmentPricing.js";
import {
  canSubmitAccountEnrollment,
  getLockedDistributorId,
  isDistributorLockedForEnrollment,
} from "./accountEnrollmentAccess.js";
import {
  ENROLLMENT_ERROR_CODES,
  checkAccountDuplicates,
  createAccountEnrollmentRequest,
  getDistributorActiveManufacturers,
  getDistributorOptions,
  getDpaProductGroups,
  getEnrollmentErrorCode,
  getEnrollmentErrorMessage,
  getRegionOptions,
  getStateRows,
  stateRowsToOptions,
  validateBaaFile,
} from "./accountEnrollmentService.js";
import {
  DERIVED_FIELD_HELP,
  applyDerivedToValues,
  buildStateLookup,
  getDerivedFieldFlags,
  unmappedFieldHelp,
} from "../../utils/stateDerivedFields.js";
import "./AccountEnrollment.css";

const STEP_TITLES = [
  "Account Details",
  "Choose Product Groups",
  "Set Pricing",
];

const DUPLICATE_CHECK_DEBOUNCE_MS = 500;
const MIN_DUPLICATE_NAME_LENGTH = 3;

const normalizeKey = (value) => String(value ?? "").trim().toLowerCase();

// Splits advisory matches into the ones the backend will reject outright
// (exact name / NPI / TIN on an active account) and the merely similar ones.
export const classifyDuplicateMatches = (matches, { AccountName, NPI, TIN }) => {
  const name = normalizeKey(AccountName);
  const npi = String(NPI ?? "").trim();
  const tin = String(TIN ?? "").trim();
  const blocking = [];
  const similar = [];
  (matches || []).forEach((match) => {
    const exact =
      match?.IsActive !== false &&
      ((name && normalizeKey(match?.AccountName) === name) ||
        (npi && String(match?.NPI ?? "").trim() === npi) ||
        (tin && String(match?.TIN ?? "").trim() === tin));
    (exact ? blocking : similar).push(match);
  });
  return { blocking, similar };
};

const describeMatch = (match) =>
  [
    match?.AccountName,
    match?.AccountNumber ? `#${match.AccountNumber}` : null,
    match?.DistributorName,
    match?.IsActive === false ? "inactive" : null,
  ]
    .filter(Boolean)
    .join(" · ");

// Keeps the selection in sync with a freshly loaded group list: drops groups
// that disappeared and refreshes the product codes of the ones that remain.
export const reconcileSelectedGroups = (selectedGroups, latestGroups) => {
  const latestById = new Map(
    (latestGroups || []).map((group) => [String(group.ProductGroupID), group]),
  );
  return (selectedGroups || [])
    .map((group) => latestById.get(String(group.ProductGroupID)) || null)
    .filter(Boolean);
};

const AccountEnrollmentWizard = () => {
  const navigate = useNavigate();
  const { user, isReady } = useAuth();

  const [currentStep, setCurrentStep] = React.useState(0);
  const [selectedGroups, setSelectedGroups] = React.useState([]);
  const [pricing, setPricing] = React.useState(createEmptyPricingState);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [baaFile, setBaaFile] = React.useState(null);
  const [baaError, setBaaError] = React.useState("");

  const [options, setOptions] = React.useState({
    DistributorID: [],
    RegionID: [],
    State: [],
  });
  const [stateRows, setStateRows] = React.useState([]);
  const [optionsError, setOptionsError] = React.useState("");

  const [groupsState, setGroupsState] = React.useState({
    distributorId: null,
    groups: [],
    manufacturers: [],
    loading: false,
    error: "",
  });
  const [groupsRefreshKey, setGroupsRefreshKey] = React.useState(0);

  const [duplicates, setDuplicates] = React.useState({
    blocking: [],
    similar: [],
  });

  const lockedDistributorId = getLockedDistributorId(user);
  const distributorLocked = isDistributorLockedForEnrollment(user);
  const dirtyRef = React.useRef(false);

  const initialValues = React.useMemo(
    () =>
      buildAccountEnrollmentInitialValues(
        lockedDistributorId ? { DistributorID: lockedDistributorId } : {},
      ),
    [lockedDistributorId],
  );

  React.useEffect(() => {
    if (isReady && !canSubmitAccountEnrollment(user)) {
      navigate("/", { replace: true });
    }
  }, [isReady, navigate, user]);

  // Option lists for step 1.
  React.useEffect(() => {
    if (!isReady || !canSubmitAccountEnrollment(user)) return undefined;
    let active = true;
    (async () => {
      try {
        const [distributors, regions, rows] = await Promise.all([
          getDistributorOptions(),
          getRegionOptions(),
          getStateRows(),
        ]);
        if (!active) return;
        setStateRows(rows);
        setOptions({ DistributorID: distributors, RegionID: regions, State: stateRowsToOptions(rows) });
        setOptionsError("");
      } catch (error) {
        if (!active) return;
        setOptionsError(
          getEnrollmentErrorMessage(error, "Unable to load form options."),
        );
      }
    })();
    return () => {
      active = false;
    };
  }, [isReady, user]);

  // There are no drafts (D14): warn before the page is unloaded mid-wizard.
  React.useEffect(() => {
    const handler = (event) => {
      if (!dirtyRef.current || submitting) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [submitting]);

  const distributorOptions = React.useMemo(() => {
    if (!lockedDistributorId) return options.DistributorID;
    const existing = options.DistributorID.find(
      (option) => Number(option.value) === lockedDistributorId,
    );
    return [
      existing || {
        value: lockedDistributorId,
        label: user?.DistributorName || `Distributor ${lockedDistributorId}`,
      },
    ];
  }, [lockedDistributorId, options.DistributorID, user?.DistributorName]);

  // Region / MAC / DME MAC follow the shipping State (StatesMaster): derived
  // fields are shown disabled, unmapped ones stay editable with a note.
  const stateLookup = React.useMemo(
    () => buildStateLookup(stateRows, options.RegionID),
    [stateRows, options.RegionID],
  );

  const loadGroupsForDistributor = React.useCallback(async (distributorId) => {
    setGroupsState((current) => ({
      ...current,
      distributorId,
      loading: true,
      error: "",
    }));
    try {
      const [groups, manufacturers] = await Promise.all([
        getDpaProductGroups(distributorId),
        getDistributorActiveManufacturers(distributorId),
      ]);
      setGroupsState({ distributorId, groups, manufacturers, loading: false, error: "" });
      setSelectedGroups((current) => reconcileSelectedGroups(current, groups));
      return groups;
    } catch (error) {
      setGroupsState({
        distributorId,
        groups: [],
        manufacturers: [],
        loading: false,
        error: getEnrollmentErrorMessage(error, "Unable to load product groups."),
      });
      return null;
    }
  }, []);

  const clearDownstreamSteps = React.useCallback(() => {
    setSelectedGroups([]);
    setPricing(createEmptyPricingState());
  }, []);

  const toggleGroup = (group, selected) => {
    setSelectedGroups((current) => {
      if (selected) {
        if (current.some((item) => item.ProductGroupID === group.ProductGroupID)) {
          return current;
        }
        return [...current, group];
      }
      return current.filter((item) => item.ProductGroupID !== group.ProductGroupID);
    });
    // Deselecting must clear any pricing already entered for that group.
    if (!selected) {
      setPricing((current) => clearPricingForGroup(current, group.ProductGroupID));
    }
  };

  const toggleManufacturer = (groups, selected) => {
    groups.forEach((group) => toggleGroup(group, selected));
  };

  const handleBaaFileChange = (file) => {
    const error = validateBaaFile(file);
    setBaaError(error);
    // An invalid pick is shown but never carried into the payload.
    setBaaFile(error ? null : file);
  };

  const leaveWizard = () => {
    if (!dirtyRef.current) {
      navigate("/AccountDashboard");
      return;
    }
    Modal.confirm({
      title: "Discard this enrollment?",
      content:
        "Enrollments cannot be saved as drafts. Everything entered so far will be lost.",
      okText: "Discard",
      okButtonProps: { danger: true },
      cancelText: "Keep editing",
      onOk: () => {
        dirtyRef.current = false;
        navigate("/AccountDashboard");
      },
    });
  };

  if (!isReady || !canSubmitAccountEnrollment(user)) return null;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async (values) => {
        if (submitting) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
          const pricingPayload = buildPricingPayload(pricing, selectedGroups);
          await createAccountEnrollmentRequest({
            Account: buildAccountDetailsPayload(values),
            AccountContacts: buildAccountContactsPayload(values[ACCOUNT_CONTACTS_KEY]),
            ...pricingPayload,
            file: baaFile,
          });
          dirtyRef.current = false;
          message.success("Account enrollment submitted for approval.");
          navigate("/AccountDashboard");
        } catch (error) {
          const code = getEnrollmentErrorCode(error);
          const text = getEnrollmentErrorMessage(
            error,
            "Unable to submit the enrollment. Please try again.",
          );
          if (
            code === ENROLLMENT_ERROR_CODES.BAA_UPLOAD_FAILED ||
            code === ENROLLMENT_ERROR_CODES.INVALID_BAA_FILE ||
            code === ENROLLMENT_ERROR_CODES.INVALID_ACCOUNT_CONTACT ||
            code === ENROLLMENT_ERROR_CODES.ACCOUNT_CONTACT_EMAIL_IN_USE
          ) {
            // The BAA and the Account Contacts live on step 1; send the user
            // back to it with the message.
            setSubmitError({ step: 0, message: text });
            setCurrentStep(0);
          } else if (code === ENROLLMENT_ERROR_CODES.ACCOUNT_ALREADY_EXISTS) {
            setSubmitError({
              step: 0,
              message: text,
              matches: error?.response?.data?.matches || [],
            });
            setCurrentStep(0);
          } else if (
            code === ENROLLMENT_ERROR_CODES.PRODUCT_GROUP_CHANGED ||
            code === ENROLLMENT_ERROR_CODES.PRODUCT_GROUP_NOT_ELIGIBLE ||
            code === ENROLLMENT_ERROR_CODES.PRODUCT_NOT_IN_GROUP
          ) {
            // The available groups/products moved under us: reload them,
            // keep what still exists and send the user back to step 2.
            setSubmitError({ step: 1, message: text });
            setCurrentStep(1);
            await loadGroupsForDistributor(Number(values.DistributorID));
          } else {
            setSubmitError({ step: 2, message: text });
          }
        } finally {
          setSubmitting(false);
        }
      }}
      validationSchema={accountEnrollmentValidationSchema}
    >
      {(formik) => {
        const distributorId = Number(formik.values.DistributorID) || null;
        dirtyRef.current = formik.dirty || selectedGroups.length > 0 || Boolean(baaFile);
        const derivedFields = getDerivedFieldFlags(stateLookup, formik.values.State);
        const helpByField = {
          RegionID: derivedFields.RegionID ? DERIVED_FIELD_HELP : unmappedFieldHelp("region", formik.values.State),
          MedicareAdminContractor: derivedFields.MedicareAdminContractor
            ? DERIVED_FIELD_HELP
            : unmappedFieldHelp("Medicare Admin Contractor", formik.values.State),
          DMEMAC: derivedFields.DMEMAC ? DERIVED_FIELD_HELP : unmappedFieldHelp("DME MAC", formik.values.State),
        };

        const productGroups =
          groupsState.distributorId === distributorId ? groupsState.groups : [];
        const detailsComplete = isAccountDetailsStepComplete(
          formik.values,
          formik.errors,
        );
        const groupsComplete = selectedGroups.length > 0;
        const pricingComplete = isPricingComplete(pricing, selectedGroups);

        const stepComplete = [detailsComplete, groupsComplete, pricingComplete][
          currentStep
        ];
        const maxReachableStepIndex = !detailsComplete ? 0 : !groupsComplete ? 1 : 2;

        const handleDistributorChange = (nextValue) => {
          const next = nextValue === null || nextValue === undefined ? null : Number(nextValue);
          if (next === distributorId) return;
          if (!selectedGroups.length) {
            formik.setFieldValue("DistributorID", next);
            return;
          }
          // The group list is distributor-scoped (D7), so steps 2–3 no
          // longer apply once the distributor changes.
          Modal.confirm({
            title: "Change distributor?",
            content:
              "The selected product groups and pricing belong to the current distributor and will be cleared.",
            okText: "Change distributor",
            okButtonProps: { danger: true },
            cancelText: "Keep current",
            onOk: () => {
              clearDownstreamSteps();
              formik.setFieldValue("DistributorID", next);
            },
          });
        };

        const goNext = () => {
          if (currentStep === 0) {
            formik.setTouched(
              Object.keys(formik.values).reduce((touched, key) => {
                touched[key] = true;
                return touched;
              }, {}),
              true,
            );
            if (!detailsComplete) return;
          }
          setSubmitError(null);
          setCurrentStep((step) => Math.min(step + 1, STEP_TITLES.length - 1));
        };

        return (
          <>
            <WizardEffects
              distributorId={distributorId}
              groupsRefreshKey={groupsRefreshKey}
              loadGroupsForDistributor={loadGroupsForDistributor}
              onDuplicates={setDuplicates}
              setValues={formik.setValues}
              stateLookup={stateLookup}
              values={formik.values}
            />
            <div className="account-enrollment-page">
              <TitleBar
                entityName="Accounts"
                entityNameLink="/AccountDashboard"
                label="New Account Enrollment"
                rightFilterBarComponents={
                  <Text color="lightText" variant="smallText">
                    Step {currentStep + 1} of {STEP_TITLES.length}
                  </Text>
                }
              />

              <ConfigProvider
                theme={{
                  token: {
                    fontFamily: "Figtree, sans-serif",
                    colorText: colors["primaryColor"],
                    colorPrimary: colors["primaryColor"],
                  },
                  components: { Steps: { iconSize: 35 } },
                }}
              >
                <Steps
                  className="custom-steps"
                  current={currentStep}
                  onChange={(step) => {
                    if (step <= maxReachableStepIndex) {
                      setSubmitError(null);
                      setCurrentStep(step);
                    }
                  }}
                  items={STEP_TITLES.map((title, index) => ({
                    title,
                    disabled: index > maxReachableStepIndex,
                  }))}
                />
              </ConfigProvider>

              <div className="account-enrollment-body">
                {optionsError ? (
                  <Alert message={optionsError} showIcon type="error" />
                ) : null}
                {submitError && submitError.step === currentStep ? (
                  <Alert
                    description={
                      submitError.matches?.length ? (
                        <ul className="account-enrollment-duplicate-list">
                          {submitError.matches.map((match) => (
                            <li key={match.ID}>{describeMatch(match)}</li>
                          ))}
                        </ul>
                      ) : null
                    }
                    message={submitError.message}
                    showIcon
                    type="error"
                  />
                ) : null}

                {currentStep === 0 ? (
                  <>
                    <AccountDetailsStep
                      errors={formik.errors}
                      lockedFields={{ DistributorID: distributorLocked }}
                      optionsByField={{
                        DistributorID: distributorOptions,
                        RegionID: options.RegionID,
                        State: options.State,
                        BillingState: options.State,
                      }}
                      setFieldTouched={formik.setFieldTouched}
                      setFieldValue={(key, value) =>
                        key === "DistributorID"
                          ? handleDistributorChange(value)
                          : formik.setFieldValue(key, value)
                      }
                      touched={formik.touched}
                      values={formik.values}
                      baaError={baaError}
                      baaFile={baaFile}
                      derivedFields={derivedFields}
                      helpByField={helpByField}
                      onBaaFileChange={handleBaaFileChange}
                    />
                    {duplicates.blocking.length ? (
                      <Alert
                        data-testid="duplicate-blocking"
                        description={
                          <ul className="account-enrollment-duplicate-list">
                            {duplicates.blocking.map((match) => (
                              <li key={match.ID}>{describeMatch(match)}</li>
                            ))}
                          </ul>
                        }
                        message="An active account with this name or NPI already exists. The enrollment will be rejected as a duplicate."
                        showIcon
                        type="error"
                      />
                    ) : null}
                    {duplicates.similar.length ? (
                      <Alert
                        data-testid="duplicate-similar"
                        description={
                          <ul className="account-enrollment-duplicate-list">
                            {duplicates.similar.map((match) => (
                              <li key={match.ID}>{describeMatch(match)}</li>
                            ))}
                          </ul>
                        }
                        message="Possible duplicates — similar accounts already exist. You can still continue."
                        showIcon
                        type="warning"
                      />
                    ) : null}
                  </>
                ) : null}

                {currentStep === 1 ? (
                  <ProductGroupsStep
                    error={groupsState.error}
                    hasActiveManufacturers={groupsState.manufacturers.length > 0}
                    loading={groupsState.loading}
                    onRetry={() => setGroupsRefreshKey((key) => key + 1)}
                    onToggleGroup={toggleGroup}
                    onToggleManufacturer={toggleManufacturer}
                    productGroups={productGroups}
                    selectedIds={selectedGroups.map((group) => group.ProductGroupID)}
                  />
                ) : null}

                {currentStep === 2 ? (
                  <PricingStep
                    onChange={setPricing}
                    pricing={pricing}
                    selectedGroups={selectedGroups}
                  />
                ) : null}

                {currentStep === 1 &&
                !groupsComplete &&
                !groupsState.loading &&
                productGroups.length > 0 ? (
                  <Alert
                    message="Select at least one product group to continue."
                    showIcon
                    type="info"
                  />
                ) : null}

                <div className="account-enrollment-actions">
                  <CommonButton
                    disabled={submitting}
                    onClick={leaveWizard}
                    variant="ghost"
                  >
                    Cancel
                  </CommonButton>
                  <div className="account-enrollment-actions-right">
                    {currentStep > 0 ? (
                      <CommonButton
                        disabled={submitting}
                        icon={<LeftSmallArrowIcon />}
                        onClick={() => {
                          setSubmitError(null);
                          setCurrentStep((step) => Math.max(step - 1, 0));
                        }}
                        variant="ghost"
                      >
                        Back
                      </CommonButton>
                    ) : null}
                    {currentStep < STEP_TITLES.length - 1 ? (
                      <CommonButton
                        disabled={!stepComplete || groupsState.loading}
                        icon={<RightSmallArrowIcon />}
                        onClick={goNext}
                        variant="primary"
                      >
                        Next
                      </CommonButton>
                    ) : (
                      <CommonButton
                        disabled={!pricingComplete || submitting}
                        onClick={formik.handleSubmit}
                        variant="primary"
                      >
                        {submitting ? "Submitting…" : "Submit Enrollment"}
                      </CommonButton>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      }}
    </Formik>
  );
};

// Effects that depend on Formik values live in a child so they can use hooks
// inside the render-prop without violating the rules of hooks.
const WizardEffects = ({
  distributorId,
  groupsRefreshKey,
  loadGroupsForDistributor,
  onDuplicates,
  setValues,
  stateLookup,
  values,
}) => {
  // Keep Region / MAC / DME MAC equal to the mapping for the chosen State.
  React.useEffect(() => {
    const next = applyDerivedToValues(stateLookup, values);
    if (next !== values) setValues(next, false);
  }, [setValues, stateLookup, values]);

  // While "same as shipping" is checked, later edits to the shipping address
  // keep flowing into the (disabled) billing fields.
  React.useEffect(() => {
    if (!values[BILLING_SAME_AS_SHIPPING_KEY]) return;
    if (billingMatchesShipping(values)) return;
    setValues(applyBillingSameAsShipping(values), false);
  }, [setValues, values]);

  // Product groups are scoped to the distributor (D7): reload on change.
  React.useEffect(() => {
    if (!distributorId) return;
    loadGroupsForDistributor(distributorId);
  }, [distributorId, groupsRefreshKey, loadGroupsForDistributor]);

  // Advisory duplicate lookup (D15), debounced on Account Name / NPI. TIN is
  // still plumbed through (the backend matches on it) but the form no longer
  // collects it, so it is always empty here.
  const { AccountName, NPI, TIN } = values;
  React.useEffect(() => {
    const name = String(AccountName ?? "").trim();
    const npi = String(NPI ?? "").trim();
    const tin = String(TIN ?? "").trim();
    const usableName = name.length >= MIN_DUPLICATE_NAME_LENGTH ? name : "";
    if (!usableName && !npi && !tin) {
      onDuplicates({ blocking: [], similar: [] });
      return undefined;
    }
    let active = true;
    const timeout = setTimeout(async () => {
      try {
        const matches = await checkAccountDuplicates({
          AccountName: usableName,
          NPI: npi,
          TIN: tin,
        });
        if (!active) return;
        onDuplicates(
          classifyDuplicateMatches(matches, { AccountName: name, NPI: npi, TIN: tin }),
        );
      } catch (error) {
        // Advisory only — a failed lookup must never block the wizard.
        if (active) onDuplicates({ blocking: [], similar: [] });
      }
    }, DUPLICATE_CHECK_DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [AccountName, NPI, TIN, onDuplicates]);

  return null;
};

export default AccountEnrollmentWizard;
